import React from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslate } from 'react-admin';
import {
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { PasswordField } from '@/components/forms';
import { validateEmail } from '@/utils/validation';
import {
  ACCOUNT_STATUS_CHOICES,
  AI_PROVIDER_CHOICES,
  COMMUNICATION_TYPE_CHOICES,
  LOGGING_STATUS_CHOICES,
  MAX_ACCOUNT_NAME_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_PATTERN,
  createEmptyApplicationRow,
  createEmptyUser,
  generateApiKey,
} from '../../constants';
import type { AccountFormValues } from '../../types/account';
import { useAccountLookup } from '../../hooks/useAccountLookup';
import ApiKeyField from '../fields/ApiKeyField';
import RegistryLinkPicker from '../fields/RegistryLinkPicker';
import IpOctetInput from '../fields/IpOctetInput';
import { ProviderPriorityList } from '../fields/ProviderPriorityList';

/**
 * Renderable blocks of this step. The wizard rail mounts this component
 * several times with different subsets (Account info / Users / Cost
 * center / Templatify toggle) — hooks and handlers always run, only the
 * markup is filtered, so the intertwined watches keep behaving the same.
 */
export type AccountInfoSection =
  | 'general'
  | 'limits'
  | 'users'
  | 'costCenter'
  | 'ai'
  | 'templatifyToggle';

const ALL_SECTIONS: AccountInfoSection[] = [
  'general',
  'limits',
  'users',
  'costCenter',
  'ai',
  'templatifyToggle',
];

interface AccountInfoStepProps {
  mode: 'create' | 'edit';
  sections?: AccountInfoSection[];
}

const sectionTitleSx = {
  color: 'text.secondary',
  fontSize: 13,
  fontWeight: 500,
  letterSpacing: 0.5,
  mb: 2,
};

export const AccountInfoStep: React.FC<AccountInfoStepProps> = ({
  mode,
  sections = ALL_SECTIONS,
}) => {
  const show = (s: AccountInfoSection): boolean => sections.includes(s);
  const translate = useTranslate();
  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<AccountFormValues>();

  const { data: licenses = [] } = useAccountLookup('licenses');
  const { data: providers = [] } = useAccountLookup('providers');
  const { data: costCenters = [] } = useAccountLookup('costCenters');

  // New accounts start on the "Basic" license (matched by name — ids
  // differ between environments). Never overrides a user pick or an
  // edited account's existing license.
  React.useEffect(() => {
    if (mode !== 'create' || licenses.length === 0) return;
    if (getValues('licenseId')) return;
    const basic = licenses.find((l) => l.name?.toLowerCase() === 'basic');
    if (basic) setValue('licenseId', basic.id);
  }, [mode, licenses, getValues, setValue]);

  // Build a "<Field name> is required" message from the i18n bag. Keeps
  // every required-validation message self-describing instead of generic
  // "Required" / "Field is required".
  const requiredMsg = (fieldKey: string): string =>
    translate('resources.accounts.validation.required', {
      field: translate(`resources.accounts.fields.${fieldKey}`),
    });

  const rateLimitApplies = useWatch({ control, name: 'rateLimitApplies' });
  const whitelistApplies = useWatch({ control, name: 'whitelistApplies' });
  const contactCenterEnabled = useWatch({ control, name: 'contactCenterEnabled' });
  const portalEnabled = useWatch({ control, name: 'portalEnabled' });
  const automationsEnabled = useWatch({ control, name: 'automationsEnabled' });

  /**
   * Permisos que ESTA cuenta puede repartir. Sale de los productos que se estan marcando
   * en este mismo formulario, no de una lista fija: ofrecer uno que la cuenta no tiene no
   * daria error —el servidor lo recorta en silencio— y quien lo marcara se quedaria
   * creyendo que lo ha concedido. Mismo criterio que `NotifyScopes.AllowedFor`.
   */
  /**
   * Los permisos que esta cuenta puede repartir, segun lo que se este contratando en este
   * mismo formulario. La LISTA y la regla vienen del backend (`GET /v1/scopes`): aqui solo
   * se cruza con las casillas de producto.
   *
   * Antes estaban las dos cosas escritas a mano, y el dia que se anadio un permiso llego al
   * backend pero aqui no salio la casilla, sin ningun error. Ofrecer uno que la cuenta no
   * tiene tampoco daria error -el servidor lo recorta en silencio- y quien lo marcara se
   * quedaria creyendo que lo ha concedido.
   */
  const { data: scopeCatalog = [] } = useAccountLookup('scopeCatalog');
  const allowedUserScopes = React.useMemo(() => {
    const contratado = new Set<string>();
    if (portalEnabled) contratado.add('portal');
    if (contactCenterEnabled) contratado.add('contact-center');
    if (automationsEnabled) contratado.add('automations');
    return scopeCatalog.filter((s) => contratado.has(s.product)).map((s) => s.code);
  }, [scopeCatalog, portalEnabled, contactCenterEnabled, automationsEnabled]);
  const applications = useWatch({ control, name: 'applications' }) ?? [];
  const { data: notifyApps = [] } = useAccountLookup('notifyApps');

  const selectedNotifyAppIds = applications
    .filter((a) => a.notify && a.applicationId)
    .map((a) => a.applicationId);

  const handleNotifyAppsChange = (ids: string[]): void => {
    const kept = applications.filter(
      (a) => a.notify && a.applicationId && ids.includes(a.applicationId)
    );
    const keptIds = kept.map((a) => a.applicationId);
    const added = ids
      .filter((id) => !keptIds.includes(id))
      .map((id) => {
        const opt = notifyApps.find((o) => o.id === id);
        return {
          ...createEmptyApplicationRow(),
          notify: true,
          applicationId: id,
          name: opt?.name ?? '',
          tag: opt?.tag ?? ('' as const),
          options: notifyApps,
          // Existing notify app — the backend enum rejects an empty
          // syncStatus; templatify linkage travels via its own picker.
          status: 'notifyOnly' as const,
          isApplicationIntegration: Boolean(useTemplatify),
        };
      });
    // Templatify-side rows (managed in the Applications step) survive.
    const templatifyRows = applications.filter(
      (a) => !a.notify && (a.templatify || a.applicationId)
    );
    const next = [...kept, ...added, ...templatifyRows];
    setValue(
      'applications',
      next.length > 0 ? next : [createEmptyApplicationRow()],
      { shouldDirty: true }
    );
  };
  const useTemplatify = useWatch({ control, name: 'useTemplatify' });
  const accountName = useWatch({ control, name: 'accountName' });

  // AI section: watch the toggle so the dependent fields (provider + keys)
  // only render when the account is allowed to use AI.
  const aiAvailable = useWatch({ control, name: 'aiAvailable' });
  // True cuando la cuenta ya tiene una api-key de IA guardada (viene del detalle; la key en
  // sí nunca vuelve). Si NO la tiene y se activa la IA, hay que exigirla —así no se guarda el
  // estado a medias "IA disponible sin key" que deja la IA inservible.
  const aiApiKeyConfigured = useWatch({ control, name: 'aiApiKeyConfigured' });
  const aiKeyRequired = Boolean(aiAvailable) && !aiApiKeyConfigured;
  // OpenRouter sirve chat, no audio. Con ese proveedor y sin key de transcripcion propia, las
  // notas de voz dejan de transcribirse SIN AVISO: el sistema degrada con gracia y nadie se
  // entera. Con Groq no pasa, porque la misma key sirve para las dos cosas.
  const aiProvider = useWatch({ control, name: 'aiProvider' });
  const aiTranscriptionApiKey = useWatch({ control, name: 'aiTranscriptionApiKey' });
  const faltaKeyDeTranscripcion =
    aiProvider === 'openrouter' && !aiTranscriptionApiKey;

  // Clear the AI config when the operator un-checks "can use AI" so we never
  // submit a stale provider/key (mirrors handleUseTemplatifyChange).
  const handleAiAvailableChange = (checked: boolean): void => {
    setValue('aiAvailable', checked, { shouldDirty: true });
    if (!checked) {
      setValue('aiProvider', '', { shouldDirty: true });
      setValue('aiApiKey', '', { shouldDirty: true });
      setValue('aiTranscriptionApiKey', '', { shouldDirty: true });
    }
  };

  // Reset Templatify-step + Applications fields ONLY when the user
  // actually flicks the Use Templatify toggle. We do this in the toggle's
  // onChange (below) instead of a `useEffect` keyed on `useTemplatify` —
  // the effect-based version misfires on remount (e.g. Back navigation
  // → AccountInfoStep mounts → useWatch's initial-vs-subscribed value
  // can yield a spurious dep change → reset wipes user data on steps 2/3).
  // Direct onChange = single source of truth, fires only on real toggle.
  const handleUseTemplatifyChange = (checked: boolean) => {
    setValue('useTemplatify', checked, { shouldDirty: true });
    setValue('applications', [createEmptyApplicationRow()], {
      shouldDirty: true,
    });
    if (checked) {
      setValue('accountMode', 'new', { shouldDirty: true });
      setValue('autoCreateEnabled', true, { shouldDirty: true });
      setValue('autoCreateGlobal', false, { shouldDirty: true });
      setValue('rmkTemplateAccountName', accountName ?? '', {
        shouldDirty: true,
      });
      setValue('rmkTemplateApiKey', generateApiKey(), { shouldDirty: true });
      setValue('rmkExistTemplateAccount', null, { shouldDirty: true });
      setValue('rmkExistTemplateApiKey', '', { shouldDirty: true });
      setValue('isUpdateTemplateApiKey', false, { shouldDirty: true });
    } else {
      setValue('accountMode', '', { shouldDirty: true });
      setValue('autoCreateEnabled', false, { shouldDirty: true });
      setValue('autoCreateGlobal', false, { shouldDirty: true });
      setValue('rmkTemplateAccountName', '', { shouldDirty: true });
      setValue('rmkTemplateApiKey', '', { shouldDirty: true });
      setValue('rmkExistTemplateAccount', null, { shouldDirty: true });
      setValue('rmkExistTemplateApiKey', '', { shouldDirty: true });
      setValue('isUpdateTemplateApiKey', false, { shouldDirty: true });
    }
  };

  const {
    fields: userFields,
    append: appendUser,
    remove: removeUser,
  } = useFieldArray({ control, name: 'users' });

  return (
    <Box>
      {show('general') && (
      <>
      {/* ============== GENERAL ============== */}
      <Typography sx={sectionTitleSx}>
        {translate('resources.accounts.sections.general')}
      </Typography>
      <Grid container spacing={{ xs: 1.5, sm: 3 }}>
        <Grid item xs={12} md={4}>
          <Controller
            name="accountName"
            control={control}
            rules={{
              required: requiredMsg('accountName'),
              maxLength: {
                value: MAX_ACCOUNT_NAME_LENGTH,
                message: translate('validation.maxLength', {
                  max: MAX_ACCOUNT_NAME_LENGTH,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                required
                label={translate('resources.accounts.fields.accountName')}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="directoryId"
            control={control}
            render={({ field }) => (
              <RegistryLinkPicker
                value={field.value ?? null}
                onChange={field.onChange}
                recordId={mode === 'edit' ? getValues('id') : undefined}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="providerIds"
            control={control}
            rules={{
              validate: (v) =>
                (v && v.length > 0) || requiredMsg('provider'),
            }}
            render={({ field, fieldState }) => {
              const selectedIds: string[] = Array.isArray(field.value)
                ? field.value
                : [];
              // Providers still mapped to the account but no longer in the active
              // list (e.g. deactivated) would otherwise render as a bare GUID with
              // no menu item to untick — surface them so they can be removed.
              const knownIds = new Set(providers.map((p) => p.id));
              const orphanIds = selectedIds.filter((id) => !knownIds.has(id));
              return (
                <TextField
                  {...field}
                  // MUI devuelve la seleccion en el orden de los items del menu, no
                  // en el de eleccion: sin esto, marcar un proveedor reordenaria la
                  // prioridad que el usuario acaba de fijar abajo.
                  onChange={(e) => {
                    const next = e.target.value as unknown as string[];
                    const kept = selectedIds.filter((id) => next.includes(id));
                    const added = next.filter((id) => !selectedIds.includes(id));
                    field.onChange([...kept, ...added]);
                  }}
                  select
                  fullWidth
                  required
                  label={translate('resources.accounts.fields.provider')}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? ' '}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) =>
                      (selected as string[])
                        .map((id) => {
                          const p = providers.find((x) => x.id === id);
                          return p
                            ? p.providerName
                            : translate('resources.accounts.fields.providerUnavailable', {
                                _: 'Unavailable provider',
                              });
                        })
                        .join(', '),
                  }}
                >
                  {providers.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.providerName}
                    </MenuItem>
                  ))}
                  {orphanIds.map((id) => (
                    <MenuItem key={id} value={id} sx={{ color: 'warning.main' }}>
                      {translate('resources.accounts.fields.providerUnavailable', {
                        _: 'Unavailable provider',
                      })}{' '}
                      — {id} ({translate('ra.action.remove', { _: 'Remove' })})
                    </MenuItem>
                  ))}
                </TextField>
              );
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="providerIds"
            control={control}
            render={({ field }) => (
              <ProviderPriorityList
                value={Array.isArray(field.value) ? field.value : []}
                providers={providers}
                onChange={field.onChange}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ApiKeyField
            name="apiKey"
            label={translate('resources.accounts.fields.apiKey')}
            required={mode === 'create'}
            regeneratable={mode === 'edit'}
            fullWidth
            helperText={
              mode === 'edit'
                ? translate('resources.accounts.help.sensitiveApiKeyOnEdit')
                : ' '
            }
            rules={
              mode === 'create'
                ? { required: requiredMsg('apiKey') }
                : undefined
            }
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* ============== CONFIGURATION ============== */}
      <Typography sx={sectionTitleSx}>
        {translate('resources.accounts.sections.configuration')}
      </Typography>
      <Grid container spacing={{ xs: 1.5, sm: 3 }}>
        <Grid item xs={12} md={4}>
          <Controller
            name="licenseId"
            control={control}
            rules={{ required: requiredMsg('license') }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                fullWidth
                required
                label={translate('resources.accounts.fields.license')}
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
              >
                {licenses.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="accountStatus"
            control={control}
            rules={{ required: requiredMsg('accountStatus') }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                fullWidth
                required
                label={translate('resources.accounts.fields.accountStatus')}
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
              >
                {ACCOUNT_STATUS_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {translate(c.name)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="loggingStatus"
            control={control}
            rules={{ required: requiredMsg('loggingStrategy') }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                fullWidth
                required
                label={translate('resources.accounts.fields.loggingStrategy')}
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
              >
                {LOGGING_STATUS_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {translate(c.name)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="logRetentionDays"
            control={control}
            rules={{
              min: {
                value: 0,
                message: translate('resources.accounts.validation.logRetentionMin'),
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? null : Number(e.target.value),
                  )
                }
                type="number"
                fullWidth
                label={translate('resources.accounts.fields.logRetentionDays')}
                placeholder={translate(
                  'resources.accounts.fields.logRetentionDaysPlaceholder',
                )}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0 }}
                error={Boolean(fieldState.error)}
                helperText={
                  fieldState.error?.message ??
                  translate('resources.accounts.fields.logRetentionDaysHelp')
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="validityTimeFrame"
            control={control}
            rules={{ required: requiredMsg('validityTimeFrame') }}
            render={({ field, fieldState }) => {
              // Past-date check is informational only — it must NOT
              // contribute to RHF's validation result, otherwise Next
              // would be blocked. The wizard's Submit button reads the
              // same condition independently and disables itself.
              const isPast = (() => {
                if (!field.value) return false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const picked = new Date(`${field.value}T00:00:00`);
                picked.setHours(0, 0, 0, 0);
                return picked < today;
              })();
              return (
                <TextField
                  {...field}
                  type="date"
                  fullWidth
                  required
                  value={field.value ?? ''}
                  label={translate('resources.accounts.fields.validityTimeFrame')}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().slice(0, 10) }}
                  error={Boolean(fieldState.error) || isPast}
                  helperText={
                    fieldState.error?.message ??
                    (isPast
                      ? translate('resources.accounts.validation.pastDateNotAllowed')
                      : translate('resources.accounts.placeholders.dateFormat'))
                  }
                />
              );
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="communicationTypes"
            control={control}
            rules={{
              validate: (v) =>
                (v && v.length > 0) || requiredMsg('communicationTypes'),
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                fullWidth
                required
                label={translate('resources.accounts.fields.communicationTypes')}
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? ' '}
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (selected) =>
                    (selected as string[])
                      .map((id) =>
                        translate(`resources.accounts.channels.${id}`)
                      )
                      .join(', '),
                }}
              >
                {COMMUNICATION_TYPE_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {translate(c.name)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        {/* Applications linked to this account (multi-select). Keeps the
            same `applications` rows the Applications step manages — rows
            added here are notify-side; templatify-side rows are preserved. */}
        <Grid item xs={12} md={4}>
          <Autocomplete
            multiple
            options={notifyApps}
            getOptionLabel={(a) => a.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={notifyApps.filter((a) =>
              selectedNotifyAppIds.includes(a.id)
            )}
            onChange={(_e, picked) =>
              handleNotifyAppsChange(picked.map((p) => p.id))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('resources.accounts.fields.applications', {
                  _: 'Applications',
                })}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
      </Grid>
      </>
      )}

      {show('ai') && (
      <>
      {/* ============== AI ============== */}
      <Typography sx={sectionTitleSx}>
        {translate('resources.accounts.sections.ai')}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(aiAvailable)}
            onChange={(_, checked) => handleAiAvailableChange(checked)}
          />
        }
        label={translate('resources.accounts.fields.aiAvailable')}
      />
      {aiAvailable && (
        <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={4}>
            <Controller
              name="aiProvider"
              control={control}
              rules={{ required: requiredMsg('aiProvider') }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  required
                  label={translate('resources.accounts.fields.aiProvider')}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? ' '}
                >
                  {AI_PROVIDER_CHOICES.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ApiKeyField
              name="aiApiKey"
              label={translate('resources.accounts.fields.aiApiKey')}
              required={aiKeyRequired}
              regeneratable={mode === 'edit' && aiApiKeyConfigured}
              fullWidth
              helperText={
                aiApiKeyConfigured
                  ? translate('resources.accounts.help.sensitiveApiKeyOnEdit')
                  : translate('resources.accounts.help.aiApiKey')
              }
              rules={
                aiKeyRequired
                  ? { required: requiredMsg('aiApiKey') }
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ApiKeyField
              name="aiTranscriptionApiKey"
              label={translate('resources.accounts.fields.aiTranscriptionApiKey')}
              regeneratable={mode === 'edit'}
              fullWidth
              helperText={translate(
                faltaKeyDeTranscripcion
                  ? 'resources.accounts.help.aiTranscriptionNeededForOpenRouter'
                  : 'resources.accounts.help.aiTranscriptionApiKey'
              )}
            />
          </Grid>
        </Grid>
      )}
      <Divider sx={{ my: 3 }} />
      </>
      )}

      {show('templatifyToggle') && (
      <>
      {/* ============== TEMPLATIFY (toggle only) ============== */}
      <Typography sx={sectionTitleSx}>
        {translate('resources.accounts.sections.templatify')}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(useTemplatify)}
            onChange={(_, checked) => handleUseTemplatifyChange(checked)}
          />
        }
        label={translate('resources.accounts.options.useTemplatify')}
      />
      </>
      )}

      {(show('limits') || show('users') || show('costCenter')) && (
      <>
      {/* MUI Checkbox stays uncontrolled when only `register(...)` is
          spread on it — RHF's register provides name/onChange/onBlur/ref
          but no `checked` prop, so the visual state never reflects the
          loaded form value. We pass `checked` explicitly from the watched
          form value alongside `register` so the visual state stays in
          sync (especially on Edit-load). */}
      <Grid container spacing={{ xs: 0.5, sm: 2 }}>
        {show('limits') && (
        <>
        <Grid item xs={12} sm={6} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                {...register('rateLimitApplies')}
                checked={Boolean(rateLimitApplies)}
              />
            }
            label={translate('resources.accounts.options.rateLimitApplies')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                {...register('whitelistApplies')}
                checked={Boolean(whitelistApplies)}
              />
            }
            label={translate('resources.accounts.options.whitelistApplies')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                {...register('contactCenterEnabled')}
                checked={Boolean(contactCenterEnabled)}
              />
            }
            label={translate('resources.accounts.options.contactCenterEnabled')}
          />
        </Grid>
        {/* Productos contratados. Son el techo de los permisos de los usuarios de la
            cuenta: al quitar uno, sus usuarios lo pierden solos. */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                {...register('portalEnabled')}
                checked={Boolean(portalEnabled)}
              />
            }
            label={translate('resources.accounts.options.portalEnabled')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                {...register('automationsEnabled')}
                checked={Boolean(automationsEnabled)}
              />
            }
            label={translate('resources.accounts.options.automationsEnabled')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            {...register('maxUsers')}
            fullWidth
            type="number"
            inputProps={{ min: 0 }}
            label={translate('resources.accounts.fields.maxUsers')}
            InputLabelProps={{ shrink: true }}
            helperText={translate('resources.accounts.help.maxUsers')}
          />
        </Grid>
        {contactCenterEnabled && (
        <Grid item xs={12} sm={9} md={6}>
          <TextField
            {...register('contactCenterReplyToEmail')}
            fullWidth
            type="email"
            label={translate('resources.accounts.fields.contactCenterReplyToEmail')}
            InputLabelProps={{ shrink: true }}
            helperText={translate('resources.accounts.help.contactCenterReplyToEmail')}
          />
        </Grid>
        )}
        </>
        )}
      </Grid>

      {/* Cost Center — optional dropdown, no toggle. Picking a value turns
          the assignment on; the empty option clears it. */}
      {show('costCenter') && (
        <Grid container spacing={3} sx={{ mt: show('limits') ? 1 : 0 }}>
          <Grid item xs={12} md={6}>
            <Controller
              name="costCenterId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  label={translate('resources.accounts.fields.costCenter')}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">—</MenuItem>
                  {costCenters.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      )}

      {show('limits') && rateLimitApplies && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Controller
              name="rateLimitPerMin"
              control={control}
              rules={{
                required: requiredMsg('rateLimitPerMin'),
                min: {
                  value: 1,
                  message: translate(
                    'resources.accounts.validation.rateLimitGtZero',
                    {
                      field: translate(
                        'resources.accounts.fields.rateLimitPerMin'
                      ),
                    }
                  ),
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth
                  required
                  label={translate('resources.accounts.fields.rateLimitPerMin')}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? ' '}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="rateLimitPerDay"
              control={control}
              rules={{
                required: requiredMsg('rateLimitPerDay'),
                min: {
                  value: 1,
                  message: translate(
                    'resources.accounts.validation.rateLimitGtZero',
                    {
                      field: translate(
                        'resources.accounts.fields.rateLimitPerDay'
                      ),
                    }
                  ),
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth
                  required
                  label={translate('resources.accounts.fields.rateLimitPerDay')}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? ' '}
                />
              )}
            />
          </Grid>
        </Grid>
      )}

      {show('limits') && whitelistApplies && (
        <Box sx={{ mt: 2 }}>
          <IpOctetInput />
        </Box>
      )}

      {show('users') && (
        <Box sx={{ mt: 3, bgcolor: 'grey.100', p: { xs: 1.5, sm: 2 }, borderRadius: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={() => appendUser(createEmptyUser())}
            sx={{
              all: 'unset',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              px: 2,
              py: 1,
              borderRadius: 1,
              cursor: 'pointer',
              fontWeight: 600,
              mb: 2,
              display: { xs: 'block', sm: 'inline-block' },
              textAlign: 'center',
              width: { xs: '100%', sm: 'auto' },
              boxSizing: 'border-box',
            }}
          >
            + {translate('resources.accounts.actions.addUser')}
          </Box>
          {userFields.map((u, i) => (
            <Box
              key={u.id}
              sx={{
                bgcolor: 'background.paper',
                p: 2,
                mb: 2,
                borderRadius: 1,
                position: 'relative',
              }}
            >
              {userFields.length > 0 && (
                <IconButton
                  size="small"
                  onClick={() => removeUser(i)}
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                  aria-label="remove user"
                >
                  <CloseIcon fontSize="small" sx={{ color: 'error.main' }} />
                </IconButton>
              )}
              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 3 }}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`users.${i}.name` as const}
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={translate('resources.accounts.fields.name')}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? ' '}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`users.${i}.surname` as const}
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={translate('resources.accounts.fields.surname')}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? ' '}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`users.${i}.email` as const}
                    control={control}
                    rules={{
                      required: requiredMsg('email'),
                      validate: (value) => {
                        const key = validateEmail(value ?? '');
                        return key == null ? true : translate(key);
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        type="email"
                        fullWidth
                        required
                        label={translate('resources.accounts.fields.email')}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? ' '}
                      />
                    )}
                  />
                </Grid>
                {/*
                  Antes habia aqui un selector de canales por usuario. Se quito: no lo leia
                  nadie —el unico codigo que lo miraba no se llamaba desde ningun sitio y
                  resolvia la cuenta por una tabla vacia—, asi que hacia creer que limitaba
                  los canales de esa persona sin limitar nada. En su lugar van los permisos,
                  que si mandan.
                */}
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                    {translate('resources.accounts.fields.userScopes')}
                  </Typography>
                  <Controller
                    name={`users.${i}.scopes` as const}
                    control={control}
                    render={({ field }) => (
                      <FormGroup row>
                        {allowedUserScopes.map((sc) => (
                          <FormControlLabel
                            key={sc}
                            control={
                              <Checkbox
                                size="small"
                                checked={(field.value ?? []).includes(sc)}
                                onChange={(_e, checked) =>
                                  field.onChange(
                                    checked
                                      ? [...(field.value ?? []), sc]
                                      : (field.value ?? []).filter((x: string) => x !== sc)
                                  )
                                }
                              />
                            }
                            label={translate(`resources.user-management.scopes.${sc}`)}
                          />
                        ))}
                        {/* Dos causas distintas y hay que decir cual: si el catalogo no ha
                            llegado, culpar a los productos manda a buscar donde no es. */}
                        {allowedUserScopes.length === 0 && scopeCatalog.length === 0 && (
                          <Typography sx={{ fontSize: 12.5, color: 'warning.main' }}>
                            {translate('resources.accounts.help.scopeCatalogUnavailable')}
                          </Typography>
                        )}
                        {allowedUserScopes.length === 0 && scopeCatalog.length > 0 && (
                          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                            {translate('resources.accounts.help.noProductsContracted')}
                          </Typography>
                        )}
                      </FormGroup>
                    )}
                  />
                </Grid>
                {/* Password fields shown on Create, and on Edit only for
                    newly-added users (no server `id`). Existing users keep
                    their stored password — fields stay hidden. */}
                {(mode === 'create' || !getValues(`users.${i}.id`)) && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name={`users.${i}.password` as const}
                        control={control}
                        rules={{
                          required: requiredMsg('password'),
                          minLength: {
                            value: PASSWORD_MIN_LENGTH,
                            message: translate(
                              'resources.accounts.validation.passwordMinLength',
                              { min: PASSWORD_MIN_LENGTH }
                            ),
                          },
                          pattern: {
                            value: PASSWORD_PATTERN,
                            message: translate(
                              'resources.accounts.validation.passwordPattern'
                            ),
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <PasswordField
                            {...field}
                            fullWidth
                            required
                            label={translate(
                              'resources.accounts.fields.password'
                            )}
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message ?? ' '}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name={`users.${i}.confirmPassword` as const}
                        control={control}
                        rules={{
                          required: requiredMsg('confirmPassword'),
                          validate: (value, formValues) => {
                            const pw = formValues.users?.[i]?.password;
                            return (
                              value === pw ||
                              translate(
                                'resources.accounts.validation.passwordsMismatch'
                              )
                            );
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <PasswordField
                            {...field}
                            fullWidth
                            required
                            label={translate(
                              'resources.accounts.fields.confirmPassword'
                            )}
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message ?? ' '}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              {errors.users?.[i] && (
                <Typography variant="caption" color="error">
                  {/* aggregate message surfaced via each field's helperText */}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
      </>
      )}
    </Box>
  );
};

export default AccountInfoStep;
